import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatOdds } from '@/lib/format-odds';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

// Signals list — every model target the scanner is tracking. Includes
// manual picks (modelId='manual') and any model that ever pushed.
//
// Filters:
//   - modelId        — text input (exact match server-side)
//   - matchId        — pasted manually (V2: replace with a search picker)
//   - includeSuperseded — toggle; default false hides superseded versions

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export function SignalsListPage() {
  const [modelId, setModelId] = useState<string>('all');
  const [matchId, setMatchId] = useState('');
  const [includeSuperseded, setIncludeSuperseded] = useState<'no' | 'yes'>('no');

  const signals = trpc.signals.list.useQuery({
    modelId: modelId === 'all' ? undefined : modelId,
    matchId: matchId.trim().length === 36 ? matchId.trim() : undefined,
    includeSuperseded: includeSuperseded === 'yes',
    limit: 200,
  });

  // Match metadata lookup so the table can render names instead of UUIDs.
  const matches = trpc.matches.search.useQuery(
    {
      limit: 200,
      fromUtc: new Date(Date.now() - ONE_DAY_MS).toISOString(),
      toUtc: new Date(Date.now() + 7 * ONE_DAY_MS).toISOString(),
    },
    { staleTime: 60_000 },
  );
  const matchById = useMemo(() => {
    const map = new Map<string, { p1Name: string | null; p2Name: string | null }>();
    for (const m of matches.data ?? []) {
      map.set(m.id, { p1Name: m.p1Name, p2Name: m.p2Name });
    }
    return map;
  }, [matches.data]);

  // Distinct model ids the operator can pick from (built from the result set).
  const modelIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of signals.data ?? []) set.add(s.modelId);
    return [...set].sort();
  }, [signals.data]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-brand text-3xl tracking-[0.25em] text-foreground">PICKS DEL MODELO</h1>
        <p className="text-xs text-muted-foreground">
          Targets activos (modelo + picks que cargaste manual). Filtrá por modelo, partido o ver
          histórico.
        </p>
      </header>

      <Card className="grid gap-3 p-4 md:grid-cols-3">
        <div className="grid gap-1.5">
          <Label htmlFor="sig-model">Modelo</Label>
          <Select value={modelId} onValueChange={setModelId}>
            <SelectTrigger id="sig-model">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {modelIds.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sig-match">Match id (UUID)</Label>
          <Input
            id="sig-match"
            value={matchId}
            onChange={(e) => setMatchId(e.target.value)}
            placeholder="opcional"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="sig-super">Incluir superseded</Label>
          <Select
            value={includeSuperseded}
            onValueChange={(v) => setIncludeSuperseded(v as 'no' | 'yes')}
          >
            <SelectTrigger id="sig-super">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no">No (sólo activos)</SelectItem>
              <SelectItem value="yes">Sí (histórico)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {signals.isError && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{signals.error.message}</AlertDescription>
        </Alert>
      )}
      {signals.isLoading && <Skeleton className="h-64" />}

      {signals.data && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Match</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead>Mercado · Selección</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead className="text-right">Confidence</TableHead>
                <TableHead className="text-right">Vence</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signals.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    Sin signals para los filtros aplicados.
                  </TableCell>
                </TableRow>
              )}
              {signals.data.map((s) => {
                const meta = matchById.get(s.canonicalMatchId);
                const matchTitle =
                  meta?.p1Name && meta.p2Name
                    ? `${meta.p1Name} vs ${meta.p2Name}`
                    : s.canonicalMatchId.slice(0, 8);
                return (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Link
                        to={`/matches/${s.canonicalMatchId}`}
                        className="text-sm text-foreground hover:underline"
                      >
                        {matchTitle}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-muted-foreground">
                        {s.modelId}/{s.modelVersion}
                      </span>
                    </TableCell>
                    <TableCell className="text-foreground">
                      {s.market} · {s.selection.toUpperCase()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatOdds(s.targetOdds)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {s.confidence === null ? '—' : `${(s.confidence * 100).toFixed(0)}%`}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">
                      {formatRelativeTime(s.validUntil)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          s.supersededBy
                            ? 'border-muted-foreground/40 text-muted-foreground'
                            : 'border-positive/40 text-positive bg-positive/10',
                        )}
                      >
                        {s.supersededBy ? 'superseded' : 'activo'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
